/**
 * Tailor-for-target-role Zustand store (ADR-021, ADR-071).
 *
 * Orchestrates the preview → apply flow for the AI tailoring feature.
 * Mirrors the sliced-store + immer conventions used by `useSiteStore`.
 *
 * State machine:
 *   idle ──runTailor──▶ loading ──ok──▶ ready ──applySelected──▶ applying ──ok──▶ idle
 *                           └──err──▶ error                          └──err──▶ error
 *
 * `selectedPatchIds` is stored as a plain object keyed by patch_id (Immer does
 * not play well with native `Set` mutations). The store exposes it as a
 * `Set<string>` via a selector helper for ergonomic component consumption.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  tailorSite,
  applyTailor,
  type TailorSuggestion,
  type TailorPreviewRequest,
  type TailorErrorCode,
} from '../../services/tailorSiteApi';
import { logger } from '../../services/logger.client';

const log = logger.child({ module: 'tailorStore' });

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export type TailorStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'applying'
  | 'error';

interface TailorState {
  status: TailorStatus;
  suggestion: TailorSuggestion | null;
  suggestionId: string | null;
  siteVersion: string | null;
  /** Internal map of accepted patch_ids. Exposed via `useSelectedPatchIds`. */
  selectedPatchMap: Record<string, true>;
  error: string | null;
  errorCode: TailorErrorCode | null;
  /** Last request body -- used to re-run after a stale_version 409. */
  lastRequest: TailorPreviewRequest | null;
}

interface TailorActions {
  runTailor: (siteId: string, body: TailorPreviewRequest) => Promise<void>;
  togglePatch: (patchId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  clear: () => void;
  /** Returns true on success so the caller can close the drawer & refetch. */
  applySelected: (siteId: string) => Promise<boolean>;
}

export type TailorStore = TailorState & { actions: TailorActions };

/* ------------------------------------------------------------------ */
/*  Initial state                                                     */
/* ------------------------------------------------------------------ */

const initialState: TailorState = {
  status: 'idle',
  suggestion: null,
  suggestionId: null,
  siteVersion: null,
  selectedPatchMap: {},
  error: null,
  errorCode: null,
  lastRequest: null,
};

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

export const useTailorStore = create<TailorStore>()(
  immer((set, get) => ({
    ...initialState,

    actions: {
      runTailor: async (siteId, body) => {
        set((state) => {
          state.status = 'loading';
          state.error = null;
          state.errorCode = null;
          state.suggestion = null;
          state.suggestionId = null;
          state.siteVersion = null;
          state.selectedPatchMap = {};
          state.lastRequest = body;
        });

        log.info('Running tailor preview', { siteId });
        const result = await tailorSite(siteId, body);

        if (!result.ok) {
          log.warn('Tailor preview failed', { code: result.code });
          set((state) => {
            state.status = 'error';
            state.error = result.error;
            state.errorCode = result.code ?? 'unknown';
          });
          return;
        }

        const { suggestion_id, site_version, suggestions } = result.data;
        /* Pre-select all patches by default -- user can deselect as needed. */
        const preselected: Record<string, true> = {};
        for (const p of suggestions.patches) preselected[p.patch_id] = true;

        set((state) => {
          state.status = 'ready';
          state.suggestion = suggestions;
          state.suggestionId = suggestion_id;
          state.siteVersion = site_version;
          state.selectedPatchMap = preselected;
        });
        log.info('Tailor preview ready', {
          patches: suggestions.patches.length,
        });
      },

      togglePatch: (patchId) => {
        set((state) => {
          if (state.selectedPatchMap[patchId]) {
            delete state.selectedPatchMap[patchId];
          } else {
            state.selectedPatchMap[patchId] = true;
          }
        });
      },

      selectAll: () => {
        set((state) => {
          const map: Record<string, true> = {};
          if (state.suggestion) {
            for (const p of state.suggestion.patches) map[p.patch_id] = true;
          }
          state.selectedPatchMap = map;
        });
      },

      clearSelection: () => {
        set((state) => {
          state.selectedPatchMap = {};
        });
      },

      clear: () => {
        set(() => ({ ...initialState, actions: get().actions }));
      },

      applySelected: async (siteId) => {
        const {
          suggestionId,
          siteVersion,
          selectedPatchMap,
          suggestion,
        } = get();

        if (!suggestionId || !siteVersion || !suggestion) {
          log.warn('applySelected called without a ready suggestion');
          return false;
        }

        const acceptedIds = Object.keys(selectedPatchMap);
        if (acceptedIds.length === 0) {
          set((state) => {
            state.error = 'Select at least one suggestion to apply.';
            state.errorCode = 'unknown';
          });
          return false;
        }

        set((state) => {
          state.status = 'applying';
          state.error = null;
          state.errorCode = null;
        });

        log.info('Applying tailor suggestions', {
          siteId,
          count: acceptedIds.length,
        });

        const result = await applyTailor(siteId, {
          suggestion_id: suggestionId,
          site_version: siteVersion,
          accepted_patch_ids: acceptedIds,
        });

        if (!result.ok) {
          log.warn('Tailor apply failed', { code: result.code });
          set((state) => {
            state.status = 'error';
            state.error = result.error;
            state.errorCode = result.code ?? 'unknown';
          });
          return false;
        }

        log.info('Tailor apply succeeded', {
          applied: result.data.accepted_count,
        });

        /* Reset back to idle on success -- the caller is responsible for
           closing the drawer and refetching site data. */
        set(() => ({ ...initialState, actions: get().actions }));
        return true;
      },
    },
  })),
);

/* ------------------------------------------------------------------ */
/*  Selector hooks                                                    */
/* ------------------------------------------------------------------ */

export function useTailorActions() {
  return useTailorStore((s) => s.actions);
}

/** Returns a fresh `Set<string>` derived from the internal map. */
export function useSelectedPatchIds(): Set<string> {
  const map = useTailorStore((s) => s.selectedPatchMap);
  return new Set(Object.keys(map));
}
